<template>
  <div class="hs-dropdown w-full relative" v-preline>
    <!-- Select Button -->
    <button
      :id="`select-${id}`"
      type="button"
      class="hs-dropdown-toggle w-full inline-flex items-center justify-between gap-2 rounded-lg border py-2 px-3 shadow-xs text-sm font-medium focus:outline-none disabled:opacity-50 disabled:pointer-events-none transition-all"
      :class="[
        variant === 'default'
          ? 'bg-secondary-50 border-gray-200 text-gray-800 hover:bg-gray-50'
          : '',
        variant === 'outline' ? 'bg-white border-gray-800 text-gray-800 hover:bg-gray-50' : '',
        variant === 'success' ? 'bg-white border-primary text-gray-800 hover:bg-gray-50' : '',
        variant === 'dark' ? 'bg-background-color border-gray-200 text-gray-800 hover:bg-gray-700' : '',
        disabled ? 'cursor-not-allowed opacity-70' : '',
        error ? 'border-red-500' : ''
      ]"
      aria-expanded="false"
      :disabled="disabled"
    >
      <div class="flex items-center gap-2 overflow-hidden">
        <component
          v-if="selectedOption && selectedOption.icon"
          :is="selectedOption.icon"
          class="flex-shrink-0 size-4"
        />
        <span class="truncate">{{ selectedOption ? selectedOption.label : placeholder }}</span>
      </div>
      <ChevronDown class="flex-shrink-0 size-4 hs-dropdown-open:rotate-180 transition-transform" />
    </button>

    <!-- Dropdown Menu -->
    <div
      class="hs-dropdown-menu transition-[opacity,margin] duration hs-dropdown-open:opacity-100 opacity-0 hidden min-w-60 bg-white shadow-md rounded-lg mt-2 p-1 space-y-0.5 z-1000"
      :class="[menuClass]"
      :aria-labelledby="`select-${id}`"
    >
      <div v-if="options && options.length > 0">
        <button
          v-for="option in options"
          :key="option.value"
          @click="selectOption(option)"
          class="w-full flex items-center gap-x-2 py-2 px-3 rounded-lg text-sm text-gray-800 hover:bg-gray-100 focus:outline-none"
          :class="{ 'bg-gray-100 text-primary': selectedValue === option.value }"
          type="button"
        >
          <component v-if="option.icon" :is="option.icon" class="flex-shrink-0 size-4" />
          <span>{{ option.label }}</span>
        </button>
      </div>
      <div v-else class="py-2 px-3 text-sm text-gray-500">{{ $t('common.noOptionsAvailable') }}</div>
    </div>

    <!-- Error Message -->
    <div v-if="error" class="text-xs text-red-500 mt-1">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ChevronDown } from 'lucide-vue-next'
import { v4 as uuidv4 } from 'uuid'
import { computed, onMounted, ref, watch } from 'vue'

interface SelectOption<T = string | number> {
  value: T
  label: string
  icon?: any // Component type
}

// Generate a unique ID for this select
const id = ref(uuidv4().slice(0, 8))

interface SelectProps {
  /**
   * Options for the select
   * Example: [{ value: 'btc', label: 'Bitcoin', icon: BitcoinIcon }]
   */
  options?: SelectOption[]

  /**
   * The selected value
   */
  modelValue?: string | number | null

  /**
   * Placeholder text
   */
  placeholder?: string

  /**
   * The variant of the select
   * default - Standard select with light gray border
   * outline - Select with dark border
   * success - Select with primary color border
   * dark - Select with dark background color
   */
  variant?: 'default' | 'outline' | 'success' | 'dark'

  /**
   * Whether the select is disabled
   */
  disabled?: boolean

  /**
   * Whether the select has an error
   */
  error?: boolean

  /**
   * Error message to display
   */
  errorMessage?: string

  /**
   * Additional class for dropdown menu
   */
  menuClass?: string
}

const props = withDefaults(defineProps<SelectProps>(), {
  options: () => [],
  modelValue: null,
  placeholder: undefined,
  variant: 'default',
  disabled: false,
  error: false,
  errorMessage: undefined,
  menuClass: ''
})

const emits = defineEmits<{
  (e: 'update:modelValue', value: string | number): void
  (e: 'change', value: string | number): void
}>()

// Get the currently selected option
const selectedValue = computed(() => props.modelValue)

// Find the selected option object
const selectedOption = computed(() => {
  if (selectedValue.value === null) return null
  return props.options.find((option) => option.value === selectedValue.value)
})

// Handle option selection
function selectOption(option: SelectOption) {
  emits('update:modelValue', option.value)
  emits('change', option.value)
}

// Watch for changes in options and model value
watch(
  () => props.options,
  () => {},
  { deep: true }
)

// Watch for model value changes
watch(
  () => props.modelValue,
  () => {}
)

// Initialize component when mounted
onMounted(() => {})
</script>

<style scoped>
/* Custom styles to match Figma design */
.hs-dropdown-toggle {
  min-height: 37px;
}
</style>
